import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashIdentity from 'lodash/fp/identity';

import {
  getHelpCardTitle,
} from 'common/helpCardHelpers';

import {
  SCREEN_ID_PARTNER_LISTING_EDIT,
} from 'client/constants/screenIds';

import {
  DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
} from 'common/dataTableConstants';

import {
  USER_ROLE_ADMIN,
  USER_ROLE_PARTNER,
  USER_ROLE_LANDLORD,
  hasRoleLandlord,
} from 'common/userRoles';

import {
  dataTable,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import {
  getListingFullAddressString,
  isListingSunRise,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  getPartnerSignupType,
} from 'common/partnerHelpers';

import {
  LISTING_SERVICE_TYPE_FULL_SERVICE,
  LISTING_SERVICE_TYPE_SUNRISE,
  LISTING_SERVICE_TYPE_TENANT_SCREENING,
} from 'common/listingConstants';

import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrNavigationButton from 'client/components/SrNavigationButton/SrNavigationButton';
import SrAccountManagerContactSupport from 'client/components/SrAccountManagerContactSupport/SrAccountManagerContactSupport';
import SrHelpCardSection from 'client/components/SrHelpCardSection/SrHelpCardSection';

import PartnerActions from 'client/actions/Partner';
import HelpCardActions from 'client/actions/HelpCard';
import AccountManagerActions from 'client/actions/AccountManager';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, {
  getUserLandingPage,
} from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';
import SrFlex from 'client/components/SrFlex';
import SrPartnerContactAccountManager from 'client/components/SrPartnerContactAccountManager';

function getListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };

  return dataTableSettings;
}

function getMarketingListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);

  if (!partner) {
    return null;
  }

  const dataTableSettings = {
    itemsPropName: 'marketingPartnerListing',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `marketing-partner-slug-single-${match.params.partnerSlug}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'partners.slug': match.params.partnerSlug,
      'marketingPartnerListings.listingId': listingId,
    },
  };
  return dataTableSettings;
}

const makeNavigationOptions = ({ securityDepositLabel }) => [
  {
    color:'#007AFF',
    iconUrl:'/images/house-icon-white.svg',
    to:'edit/property',
    title:'Listing & Marketing',
    subtitle:'Rent Price, Marketing Description',
    services: [
      LISTING_SERVICE_TYPE_FULL_SERVICE,
      LISTING_SERVICE_TYPE_TENANT_SCREENING,
      LISTING_SERVICE_TYPE_SUNRISE,
    ],
  },
  {
    color: '#DBB000',
    iconUrl: '/images/key-icon.svg',
    iconSize: 25,
    to: 'edit/showing-access',
    title: 'Showing & Access',
    subtitle: 'Occupant, Combo Lockbox, Codes',
    services: [LISTING_SERVICE_TYPE_FULL_SERVICE],
  },
  {
    color: '#AD00AD',
    iconUrl: '/images/checkmark-icon-thick-white.svg',
    iconSize: 25,
    to: 'edit/renter-pet-criteria',
    title: 'Renter & Pet Criteria',
    subtitle: 'Credit Score, Dogs & Cats',
    services: [LISTING_SERVICE_TYPE_FULL_SERVICE, LISTING_SERVICE_TYPE_TENANT_SCREENING],
  },
  {
    color: '#00A238',
    iconUrl: '/images/dollar-sign-icon-thick-white.svg',
    to: 'edit/fee-settings',
    title: securityDepositLabel,
    subtitle: 'Bank Account',
    services: [LISTING_SERVICE_TYPE_FULL_SERVICE],
  },
  {
    color: '#132630',
    iconUrl: '/images/document-icon-white.svg',
    iconSize: 16,
    to: 'edit/move-in',
    title: 'Lease & Move-In',
    subtitle: 'Contract, Lease Details',
    services: [LISTING_SERVICE_TYPE_FULL_SERVICE],
  },
];

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
    USER_ROLE_PARTNER,
    USER_ROLE_LANDLORD,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_LISTING_EDIT)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  accountManager: ['accountManagers', 'byListingId', match.params.listingId],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.helpCards = [
      'helpCards',
      'list',
      [SCREEN_ID_PARTNER_LISTING_EDIT, getPartnerSignupType(partner)].join('-'),
    ];
  }

  return cursors;
})
@dataTable(getMarketingListingDataTableSettings)
@dataTable(getListingDataTableSettings)
class PartnerEditListingPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Edit Listing',
  })

  static fetchData(match, inject) {
    let partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const listingId = parseInt(match.params.listingId, 10);

    return Promise.resolve()
      .then(() => {
        if (!partner) {
          return PartnerActions.loadPartnerBySlug(inject, {
            slug: match.params.partnerSlug,
          });
        }
      })
      .then(() => {
        partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);

        const promises = [
          AccountManagerActions.getByListingId(inject, { listingId }),
          HelpCardActions.getList(inject, {
            screenId: SCREEN_ID_PARTNER_LISTING_EDIT,
            partnerSignupType: getPartnerSignupType(partner),
          }),
        ];

        [
          getListingDataTableSettings({ partner, match }),
          getMarketingListingDataTableSettings({ partner, match }),
        ]
          .filter(lodashIdentity)
          .forEach((dataTableSettings) => {
            actionInitializeDataTableState(inject, dataTableSettings);
            promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
          });

        return Promise.all(promises);
      });
  }

  static propTypes = {
    match: PropTypes.object.isRequired,
    listing: PropTypes.object,
    accountManager: PropTypes.object,
    helpCards: PropTypes.array,
  }

  _shouldDisplayNavigationOption = (listing, option) => {
    const { services: optionServices } = option;
    const listingServices = listing?.services;

    if (!listingServices) {
      return true;
    }

    for (const listingService of listingServices) {
      if (optionServices.includes(listingService)) {
        return true;
      }
    }

    return false;
  }

  _getNavigationOptions = () => {
    const { listing, partner, match } = this.props;
    const { listingId } = match.params;

    const securityDepositLabel = getMarketSecurityDepositLabel(listing, true);

    const baseRedirectUrl = `/partner/${partner.slug}/listing/${listingId}`;
    return makeNavigationOptions({ securityDepositLabel }).map((option) => {
      const shouldDisplayOption = this._shouldDisplayNavigationOption(listing, option);

      if (!shouldDisplayOption) {
        return null;
      }

      return (
        <SrBox marginTop={10} key={option.title}>
          <SrNavigationButton
            color={option.color}
            iconUrl={option.iconUrl}
            iconSize={option.iconSize}
            to={`${baseRedirectUrl}/${option.to}`}
            title={option.title}
            subtitle={option.subtitle}
          />
        </SrBox>
      );
    });
  }

  _getContactInfoComponent = () => {
    const {
      listing,
      accountManager,
      marketingPartnerListing,
    } = this.props;

    if (!listing) {
      return null;
    }

    if (isListingSunRise(listing)) {
      return (
        <SrFlex width='100%' justify='center' marginTop='120px'>
          <SrPartnerContactAccountManager
            marketingPartnerListing={marketingPartnerListing}
          />
        </SrFlex>
      );
    }

    return accountManager && (
      <SrAccountManagerContactSupport
        accountManager={accountManager}
      />
    );
  }

  render() {
    const {
      match,
      listing,
      partner,
      currentUser,
      isTransitioning,
      helpCards,
    } = this.props;

    if (isTransitioning && (!partner || !listing)) {
      return null;
    }

    if (!partner || !listing) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    const { listingId } = match.params;
    const isLandlord = hasRoleLandlord(currentUser.roles) && listing?.landlordUserId === currentUser.id;
    const navigationOptionComponents = this._getNavigationOptions();
    const contactInformationComponent = this._getContactInfoComponent();

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/listing/${listingId}`}
        navbarTitle={getListingFullAddressString(listing)}
        pageHeader='Edit Listing'
        pageSubtitle={isLandlord
          ? null
          : (
            <>
              <SrText
                font='greycliff'
                fontSize='16px'
                lineHeight='25px'
                color='blueGreycliff'
                opacity='0.5'
              >
                Make updates to this listing only. Go to&nbsp;
              </SrText>
              <SrLink to={`/partner/${partner.slug}/settings`}>
                <SrText
                  font='greycliff'
                  fontSize='16px'
                  lineHeight='25px'
                  color='blue300'
                >
                  Account Settings&nbsp;
                </SrText>
              </SrLink>
              <SrText
                font='greycliff'
                fontSize='16px'
                lineHeight='25px'
                color='blueGreycliff'
                opacity='0.5'
              >
                to make global updates.
              </SrText>
            </>
          )}
      >
        <div>
          {navigationOptionComponents}
        </div>
        {contactInformationComponent}
        {helpCards && helpCards.length > 0 && (
          <SrBox marginTop='80px'>
            <SrHelpCardSection
              helpCards={helpCards}
              title={getHelpCardTitle({ partner })}
            />
          </SrBox>
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerEditListingPage.displayName = 'PartnerEditListingPage';

export default PartnerEditListingPage;
